// Thin HTTP wrapper for the query-session backend. One session per query tab.
import { api } from './api.js';
import { isIntegerType } from './format.js';

// ── Exact values for row writes ─────────────────────────────────────────────
// An integer beyond 2^53 arrives from the server as a decimal string (a JS
// number would round it). As a row KEY it must go back as an integer, not a
// string: MySQL compares an integer column with a string as DOUBLEs, so
// '9007199254740993' also matches …992. JSON from JS can't carry that number
// exactly, so the backend takes {"$int": "<digits>"} and binds an exact int.
export function keyValue(v, type) {
  if (typeof v === 'string' && isIntegerType(type) && /^\s*-?\d+\s*$/.test(v)) return { $int: v.trim() };
  return v;
}
export const keyValues = (values, types) => (values || []).map((v, i) => keyValue(v, types?.[i]));

// A BIT value typed as text ('1') is the byte 0x31 to MySQL — "Data too long"
// in strict mode, a wrong bit pattern otherwise. Send the number instead:
// decimal, b'101' / 0b101 or x'1F' / 0x1F. Anything else goes as typed (the
// server's error / warning then shows).
export function bitWriteValue(v) {
  if (v === null || v === undefined || typeof v === 'object') return v;
  const s = String(v).trim().toLowerCase();
  let m;
  try {
    if (/^\d+$/.test(s)) return { $int: BigInt(s).toString() };
    if ((m = /^b'([01]+)'$/.exec(s) || /^0b([01]+)$/.exec(s))) return { $int: BigInt('0b' + m[1]).toString() };
    if ((m = /^x'([0-9a-f]+)'$/.exec(s) || /^0x([0-9a-f]+)$/.exec(s))) return { $int: BigInt('0x' + m[1]).toString() };
  } catch (_) { /* not a number after all */ }
  return v;
}
export const writeValue = (v, type) => (/^\s*bit\b/i.test(String(type || '')) ? bitWriteValue(v) : v);
// writeValues maps a {column: value} edit through writeValue; typeOf(column) →
// the column's type.
export function writeValues(map, typeOf) {
  const out = {};
  for (const [k, v] of Object.entries(map || {})) out[k] = writeValue(v, typeOf(k));
  return out;
}

// ── Lost connections ────────────────────────────────────────────────────────
// The backend answers 410 {sessionLost} when a session's connection died with
// something at stake (a 404 means nothing ran and nothing was lost — safe to
// reconnect and retry silently). Returns the facts, or null for any other error.
export function sessionLostInfo(e) {
  if (e?.status !== 410 || !e?.payload?.sessionLost) return null;
  const p = e.payload;
  return { lostTransaction: !!p.lostTransaction, settingsReset: !!p.settingsReset, outcomeUnknown: !!p.outcomeUnknown };
}
// What to tell the user. `action` names what was running ('statement', 'commit',
// 'row edit', …).
export function sessionLostMessage(info, action = 'statement') {
  const out = ['Lost the connection to the server.'];
  if (action === 'commit' && info.outcomeUnknown) {
    out.push('The COMMIT may or may not have gone through — check the data before redoing anything.');
  } else if (info.lostTransaction) {
    out.push('The open transaction was rolled back by the server — its uncommitted changes are gone.');
  } else if (info.outcomeUnknown) {
    out.push(`The ${action} may or may not have been applied — check before running it again.`);
  }
  if (info.settingsReset) out.push("The session's autocommit setting is back to the server default.");
  return out.join(' ');
}

// ── Can Rollback undo it? ───────────────────────────────────────────────────
// true / false for a known engine, null when unknown. Writes to a
// non-transactional table (MyISAM, MEMORY, ARCHIVE, CSV, Aria…) are permanent
// even inside a transaction.
const TRANSACTIONAL_ENGINES = new Set(['innodb', 'xtradb', 'tokudb', 'rocksdb', 'ndbcluster', 'ndb', 'bdb', 'berkeleydb']);
export function isTransactionalEngine(engine) {
  if (!engine) return null;
  return TRANSACTIONAL_ENGINES.has(String(engine).toLowerCase());
}
// The consequence sentence for deleting rows in an edit grid.
export function deleteNote({ inTransaction, engine }) {
  if (!inTransaction) return 'Auto-commit is on, so this is permanent and cannot be undone.';
  const tx = isTransactionalEngine(engine);
  if (tx === false) return `The table uses ${engine}, which doesn't support transactions — this is permanent; Rollback can't undo it.`;
  if (tx === true) return 'This stays inside the open transaction — Rollback undoes it.';
  return "This stays inside the open transaction, so Rollback undoes it — unless the table's engine is non-transactional (e.g. MyISAM, MEMORY), where it's permanent.";
}
export const deleteConfirmMessage = (n, opts) => `Delete ${n} row${n === 1 ? '' : 's'}? ${deleteNote(opts)}`;

export async function createQuerySession(connectionId, database) {
  return api.post(`/api/connections/${connectionId}/query-sessions`,
    database ? { database } : {});
}

export async function closeQuerySession(sessionId) {
  // Best-effort. On page unload we re-issue via fetch keepalive.
  try {
    await api.delete(`/api/query-sessions/${sessionId}`);
  } catch (_) {}
}

// keepalive variant used during beforeunload — survives navigation.
export function closeQuerySessionBeacon(sessionId) {
  try {
    fetch(`/api/query-sessions/${sessionId}`, { method: 'DELETE', keepalive: true });
  } catch (_) {}
}

export async function useDb(sessionId, database) {
  return api.post(`/api/query-sessions/${sessionId}/use-db`, { database });
}

// safeUpdates (default true) toggles MySQL SQL_SAFE_UPDATES for this statement;
// pass false only for a write the user explicitly confirmed (one-shot override).
export async function executeQuery(sessionId, sqlText, safeUpdates = true) {
  return api.post(`/api/query-sessions/${sessionId}/execute`, { sql: sqlText, safeUpdates });
}

// Bind named query parameters as MySQL user variables (SET @name = ?) on the
// pinned session, before running a `:param`-rewritten statement. `vars` is a
// { name: value } map; values are bound by the driver (injection-safe).
export async function setSessionVars(sessionId, vars) {
  return api.post(`/api/query-sessions/${sessionId}/set-vars`, { vars });
}

export async function beginTransaction(sessionId) {
  return api.post(`/api/query-sessions/${sessionId}/begin`, {});
}
export async function commitTransaction(sessionId) {
  return api.post(`/api/query-sessions/${sessionId}/commit`, {});
}
export async function rollbackTransaction(sessionId) {
  return api.post(`/api/query-sessions/${sessionId}/rollback`, {});
}

export async function cancelQuery(sessionId) {
  return api.post(`/api/query-sessions/${sessionId}/cancel`, {});
}

// Active-sessions panel: list live query sessions (own, or all for admins) and
// force-kill one (KILLs the MySQL connection + drops the in-memory session).
export async function listQuerySessions() {
  const { sessions } = await api.get('/api/query-sessions');
  return sessions || [];
}
export async function killQuerySession(sessionId) {
  return api.post(`/api/query-sessions/${sessionId}/kill`, {});
}

// Read-only liveness of a (possibly previous) session, for honest relabelling
// of an in-flight statement after a reload. → { exists, running, inTransaction, threadId }.
export async function querySessionStatus(sessionId) {
  return api.get(`/api/query-sessions/${sessionId}/status`);
}

// Structured single-row UPDATE used by the editable grids. The server builds the
// SQL with parameter binding and returns a display-only `sql` string with values
// inlined for the log. `onError` ('stop' | 'skip') selects whether a failure
// rolls back the whole edit transaction or just this row (SAVEPOINT).
export async function updateRow(sessionId, { database, table, pkColumns, pkValues, setColumns, onError }) {
  return api.post(`/api/query-sessions/${sessionId}/update-row`, {
    database, table, pkColumns, pkValues, setColumns, onError,
  });
}

// Structured single-row INSERT used to append/insert a new row in the editable
// grids. `columns` is the user-set columns (auto-increment/generated omitted).
export async function insertRow(sessionId, { database, table, columns, onError }) {
  return api.post(`/api/query-sessions/${sessionId}/insert-row`, {
    database, table, columns, onError,
  });
}

// Bulk-row DELETE used by the editable grids when the user gutter-selects
// rows and hits the "🗑 Delete N row(s)" button. `rows` is an array of PK-value
// tuples (positional, matching pkColumns). Each row gets its own DELETE
// (parameter-bound); the response carries per-row outcomes.
export async function deleteRows(sessionId, { database, table, pkColumns, rows, onError }) {
  return api.post(`/api/query-sessions/${sessionId}/delete-rows`, {
    database, table, pkColumns, rows, onError,
  });
}

// Query-tab idle timeout (admin): minutes an idle query tab keeps its pinned
// MySQL connection before it is closed (the tab reopens one on next use).
export const getQuerySessionConfig = () => api.get('/api/admin/query-session-config');
export const setQuerySessionConfig = (idleMinutes) => api.put('/api/admin/query-session-config', { idleMinutes });
